import { defineStore } from "pinia";
import { computed, reactive, ref } from "vue";

import type { LiveConsultStore } from "@/App/components/liveConsult/types/LiveConsultStore";
import type { StartLiveConsultParameters } from "@/App/components/liveConsult/types/StartLiveConsultParameters";

import { LiveConsult } from "@/App/components/liveConsult/liveConsult";
import { liveConsultService } from "@/App/components/liveConsult/liveConsult.service";
import { confirmationModalId, useCasesStore } from "@/Pages/Cases/store";
import { showGeneralModal } from "../Dispatch/showGeneralModal";
import { DataState, Loading, LoadingFailed, LoadingSucceeded } from "@/Pages/utils/DataState";
import { canUserAnswerLiveConsult } from "./utils/canUserAnswerLiveConsult";
import type { TwiageCase } from "@/App/types/TwiageCase";
import { useUserStore } from "@/App/stores";
import { FeatureFlag } from "@/App/types/FeatureFlag";

export const useLiveConsultStore = defineStore("liveConsult", () => {
  const casesStore = useCasesStore();
  const userStore = useUserStore();

  const endLiveConsult = ({ caseId }: { caseId: string }): void => {
    if (startedLiveConsults[caseId]) {
      startedLiveConsults[caseId].hangUp();

      delete startedLiveConsults[caseId];

      liveConsultOn[caseId] = false;
    }
  };

  const liveConsultDetached = ref(false);

  const liveConsultOn: Record<string, boolean> = reactive({});
  const liveConsultStates = reactive<Record<string, DataState>>({});

  const startLiveConsult = ({ caseId, startWithVideoDisabled = false }: StartLiveConsultParameters): void => {
    startedLiveConsults[caseId] = new LiveConsult({ caseId, liveConsultService, startWithVideoDisabled });
    liveConsultOn[caseId] = true;
  };

  const createNewLiveConsult = async ({ caseId }: StartLiveConsultParameters) => {
    if (!userCanStartLiveConsult.value) {
      return;
    }

    try {
      liveConsultStates[caseId] = new Loading();
      await liveConsultService.getLiveConsultToken({ caseId });

      startLiveConsult({ caseId, startWithVideoDisabled: true });

      liveConsultStates[caseId] = new LoadingSucceeded();
    } catch (error) {
      const message = (error as Error)?.message ?? "Unable to start Live Consult";
      showGeneralModal(confirmationModalId, "Live Consult", message, () => {});

      liveConsultStates[caseId] = new LoadingFailed(message, "live-consult-error");
    }
  };

  const startedLiveConsults: LiveConsultStore["startedLiveConsults"] = {};

  const userCanAnswerLiveConsult = computed<boolean>(() =>
    canUserAnswerLiveConsult({ twiageCase: casesStore.selectedCase as TwiageCase, user: userStore.user }),
  );

  const userCanStartLiveConsult = computed<boolean>(() => userStore.isFeatureEnabled(FeatureFlag.canStartLiveConsult));

  const shouldShowLiveConsult = computed<boolean>(
    () =>
      casesStore.selectedCase?.isVideoConsultInProgress || liveConsultOn[casesStore.selectedCase?.id ?? ""] === true,
  );

  return {
    liveConsultStates,
    endLiveConsult,
    liveConsultDetached,
    liveConsultOn,
    startLiveConsult,
    startedLiveConsults,
    createNewLiveConsult,
    userCanAnswerLiveConsult,
    shouldShowLiveConsult,
    userCanStartLiveConsult,
  };
});
